'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CultureHighlight {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
}

interface EmployeeTestimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  quote: string;
}

interface CompanyCultureProps {
  highlights: CultureHighlight[];
  testimonials: EmployeeTestimonial[];
}

const CompanyCulture: React.FC<CompanyCultureProps> = ({ highlights, testimonials }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  const handleTestimonialChange = (index: number) => {
    if (!isHydrated) return;
    setActiveTestimonial(index);
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-muted to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-secondary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="SparklesIcon" size={20} className="text-brand-secondary" />
            <span className="text-sm font-cta font-semibold text-brand-secondary">Company Culture</span>
          </div>
          
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Where Innovation Meets Collaboration
          </h2>
          
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            We've built a workplace where cutting-edge technology meets human connection, creating an environment where both our team and our clients thrive.
          </p>
        </div>
        
        {/* Culture Highlights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {highlights.map((highlight) => (
            <div
              key={highlight.id}
              className="bg-card rounded-xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden group"
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={highlight.image}
                  alt={highlight.alt}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                      <Icon name={highlight.icon as any} size={20} className="text-white" variant="solid" />
                    </div>
                    <h3 className="font-headline font-bold text-xl text-white">
                      {highlight.title}
                    </h3>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <p className="font-body text-muted-foreground leading-relaxed">
                  {highlight.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Employee Testimonials */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl shadow-elevated p-8 lg:p-12">
            <div className="flex items-center justify-center mb-8">
              <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-accent" variant="solid" />
            </div>
            
            <div className="text-center mb-8">
              <p className="font-body text-xl lg:text-2xl text-foreground leading-relaxed italic mb-8">
                "{testimonials[activeTestimonial].quote}"
              </p>
              
              <div className="flex items-center justify-center space-x-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden border-4 border-accent">
                  <AppImage
                    src={testimonials[activeTestimonial].image}
                    alt={testimonials[activeTestimonial].alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="text-left">
                  <p className="font-headline font-bold text-lg text-primary">
                    {testimonials[activeTestimonial].name}
                  </p>
                  <p className="font-body text-sm text-muted-foreground">
                    {testimonials[activeTestimonial].role}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Testimonial Navigation */}
            <div className="flex items-center justify-center space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleTestimonialChange(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial
                      ? 'bg-accent w-8' :'bg-border hover:bg-muted-foreground'
                  }`}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyCulture;